import type { ReactNode } from 'react';

import type { Size } from './types';
import { General, Shapes } from '../StrokeIcons';
import Button from './Button';

export type CheckboxButtonProps = {
  /** Current setting. Drives the glyph, the fill, and `aria-pressed`. */
  checked: boolean;
  /** The label. Kept identical in both states so the control reads as a setting, not an action. */
  children: ReactNode;
  onChange: (checked: boolean) => void;
  /** State-specific copy — the label alone cannot say which way the option is set. */
  tooltip?: ReactNode;
  size?: Size;
  disabled?: boolean;
  /**
   * `square` (default): an on/off setting. `circle`: one of a set where picking one replaces the
   * others — announced as a radio, so put the set in a `role="radiogroup"`.
   */
  shape?: 'square' | 'circle';
  /** Text style: no border or fill until hovered (e.g. rows in a filter list). */
  text?: boolean;
  className?: string;
};

/**
 * A labelled on/off chip: checkbox glyph + text, filled when on.
 *
 * The labelled sibling of {@link CheckboxIconButton} (same `CheckSquare`/`Square` glyph pair, same
 * blue-on / gray-off axis), for rows of boolean options that need words rather than an icon —
 * e.g. the step editor's "Use Email Signature" and "Reply in thread".
 *
 * Why the glyph rather than fill alone: a lone chip gives the reader nothing to compare against, so
 * "off" and "a button I have not pressed yet" look identical — colour only reads as state when both
 * states happen to be on screen at once, which they usually are not. The glyph states the setting on
 * its own, and `aria-pressed` says the same thing to a screen reader.
 */
export default function CheckboxButton({
  checked,
  children,
  onChange,
  tooltip,
  size = 'sm',
  disabled,
  shape = 'square',
  text,
  className,
}: CheckboxButtonProps) {
  return (
    <Button
      size={size}
      color={checked ? 'blue' : 'gray'}
      {...(shape === 'circle' ? { role: 'radio', 'aria-checked': checked } : { 'aria-pressed': checked })}
      disabled={disabled}
      tooltip={tooltip}
      text={text}
      className={className}
      icon={
        <>
          {shape === 'square' && checked && <General.CheckSquare aria-hidden="true" />}
          {shape === 'square' && !checked && <Shapes.Square aria-hidden="true" />}
          {shape === 'circle' && checked && <General.CheckCircle aria-hidden="true" />}
          {shape === 'circle' && !checked && <Shapes.Circle aria-hidden="true" />}
        </>
      }
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onChange(!checked);
      }}>
      {children}
    </Button>
  );
}
